import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { Mono } from '../Mono';
import { ICarItem } from '../../../modules/API/Defaults/models/cars';
import TruckIcon from '../../icons/IconLib/TruckIcon';

interface Props {
    cars: ICarItem[];
    value: ICarItem | null;
    onSelect: (car: ICarItem | null) => void;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    allowClear?: boolean;
    clearLabel?: string;
    className?: string;
}

const CarSelector: React.FC<Props> = ({
    cars,
    value,
    onSelect,
    placeholder,
    disabled,
    readOnly,
    allowClear,
    clearLabel,
    className,
}) => {
    const [t] = useTranslation('selectors');

    return (
        <SearchableSelect<ICarItem>
            items={cars}
            value={value}
            onSelect={onSelect}
            getKey={(c) => c.CarID}
            getLabel={(c) => (c.CarNum != null ? c.CarNum.toString() : '')}
            getSearchText={(c) => `${c.CarNum ?? ''} ${c.DriverName ?? ''}`}
            renderItem={(c) => (
                <Row>
                    <CarNum>{c.CarNum}</CarNum>
                    <Dot aria-hidden>·</Dot>
                    <Driver>{(c.DriverName ?? '').trim()}</Driver>
                </Row>
            )}
            placeholder={placeholder ?? t('selectors_car_placeholder')}
            disabled={disabled}
            readOnly={readOnly}
            allowClear={allowClear}
            clearLabel={clearLabel ?? t('selectors_clear')}
            leadingIcon={<TruckIcon />}
            className={className}
            emptyLabel={t('selectors_car_empty')}
        />
    );
};

export default CarSelector;

const Row = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.5rem;
`;

const CarNum = styled(Mono)`
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
`;

const Dot = styled.span`
    color: ${({ theme }) => theme.colors.ink4};
`;

const Driver = styled.span`
    color: ${({ theme }) => theme.colors.ink2};
`;
